
import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, X, User, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useTranslation } from 'react-i18next';
import { usePets } from '../hooks/usePets';
import { useFeedPosts } from '../hooks/usePosts';
import { usePublicUserSearch } from '../hooks/usePublicUserSearch';
import { translateSpeciesSingular } from '@/utils/speciesTranslation';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const { pets } = usePets();
  const { data: posts } = useFeedPosts();

  // 🔒 Secure public user search
  const {
    searchResults: userResults,
    setSearchTerm: setUserSearchTerm,
    isLoading: isSearchingUsers
  } = usePublicUserSearch();

  // Derive pets/posts results via useMemo — no setState in effects, no infinite loop.
  // Preserves identical filtering logic and 5-item cap.
  const searchResults = useMemo<{ pets: any[]; posts: any[] }>(() => {
    if (!searchQuery.trim()) {
      return { pets: [], posts: [] };
    }

    const query = searchQuery.toLowerCase();

    const filteredPets = (pets ?? []).filter((pet: any) =>
      pet.name?.toLowerCase().includes(query) ||
      pet.breed?.toLowerCase().includes(query) ||
      pet.species?.toLowerCase().includes(query)
    );

    const filteredPosts = (posts ?? []).filter((post: any) =>
      post.caption?.toLowerCase().includes(query) ||
      post.pet?.name?.toLowerCase().includes(query)
    );

    return {
      pets: filteredPets.slice(0, 5),
      posts: filteredPosts.slice(0, 5),
    };
  }, [searchQuery, pets, posts]);

  // Sync the secure user search term only when the query changes.
  useEffect(() => {
    setUserSearchTerm(searchQuery.trim() ? searchQuery : '');
  }, [searchQuery, setUserSearchTerm]);

  const handleClose = () => {
    setSearchQuery('');
    setUserSearchTerm('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{t('modals.search.title')}</DialogTitle>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <DialogDescription>
            {t('modals.search.description')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('modals.search.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>

          {searchQuery && (
            <div className="space-y-4">
              {/* 🔒 NEW: Users Results - Secure public search */}
              {userResults.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">{t('modals.search.users')}</h3>
                  <div className="space-y-2">
                    {userResults.map((user) => (
                      <Link
                        key={user.id}
                        to={`/profile/${user.id}`}
                        onClick={handleClose}
                        className="block"
                      >
                        <Card className="hover:bg-gray-50 transition-colors">
                          <CardContent className="p-3">
                            <div className="flex items-center space-x-3">
                              <Avatar className="h-10 w-10" key={user.avatar_url || user.id}>
                                <AvatarImage 
                                  src={user.avatar_url || undefined} 
                                  alt={user.full_name || 'User'}
                                  loading="eager"
                                />
                                <AvatarFallback>
                                  <User className="h-5 w-5" />
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{user.full_name || t('modals.search.petiusUser')}</p>
                                {user.bio && (
                                  <p className="text-sm text-gray-500 line-clamp-1">
                                    {user.bio}
                                  </p>
                                )}
                                {user.location && (
                                  <p className="text-xs text-gray-400">
                                    📍 {user.location}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Pets Results - Existing functionality preserved */}
              {searchResults.pets.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">{t('modals.search.pets')}</h3>
                  <div className="space-y-2">
                    {searchResults.pets.map((pet) => (
                      <Link
                        key={pet.id}
                        to={`/pet/${pet.id}`}
                        onClick={handleClose}
                        className="block"
                      >
                        <Card className="hover:bg-gray-50 transition-colors">
                          <CardContent className="p-3">
                            <div className="flex items-center space-x-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={pet.photo_url} alt={pet.name} />
                                <AvatarFallback>
                                  {pet.name?.charAt(0)?.toUpperCase() || 'P'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{pet.name}</p>
                                <p className="text-sm text-gray-500">
                                  {pet.breed} • {translateSpeciesSingular(pet.species, t)}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Posts Results - Existing functionality preserved */}
              {searchResults.posts.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">{t('modals.search.posts')}</h3>
                  <div className="space-y-2">
                    {searchResults.posts.map((post) => (
                      <Link
                        key={post.id}
                        to={`/post/${post.id}`}
                        onClick={handleClose}
                        className="block"
                      >
                        <Card className="hover:bg-gray-50 transition-colors">
                          <CardContent className="p-3">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 bg-gray-200 rounded flex items-center justify-center">
                                <Heart className="h-5 w-5 text-gray-400" />
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium line-clamp-1">
                                  {post.caption || t('modals.search.memoryPost')}
                                </p>
                                <p className="text-xs text-gray-500">
                                  by {post.pet?.name || t('modals.search.unknownPet')}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Loading state for user search */}
              {isSearchingUsers && searchQuery.length >= 2 && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-300 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">{t('modals.search.searchingUsers')}</p>
                </div>
              )}

              {/* No Results - Updated to include users */}
              {searchQuery && 
               searchResults.pets.length === 0 && 
               searchResults.posts.length === 0 && 
               userResults.length === 0 &&
               !isSearchingUsers && (
                <div className="text-center py-8">
                  <Search className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                  <h3 className="text-lg font-medium text-gray-600">{t('modals.search.noResults')}</h3>
                  <p className="text-gray-500">
                    {t('modals.search.noResultsDescription')}
                  </p>
                </div>
              )}
            </div>
          )}

          {!searchQuery && (
            <div className="text-center py-8 text-gray-500">
              <Search className="mx-auto h-12 w-12 text-gray-300 mb-3" />
              <p>{t('modals.search.startTyping')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
